'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {useSearchParams} from 'next/navigation';

interface AuthContextType {
  isAuthenticated: boolean | null;
  setIsAuthenticated: (value: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const searchParams = useSearchParams();

  useEffect(() => {
    // Check URL parameters first
    const isAuthedParam = Boolean(searchParams.get('isAuthed'));

    if (isAuthedParam) {
      // Only set auth when true
      localStorage.setItem('isAuthed', JSON.stringify(true));
      setIsAuthenticated(true);
    } else {
      // Try to retrieve from localStorage if not in URL
      const storedAuth = localStorage.getItem('isAuthed');
      if (storedAuth !== null) {
        const isAuth = Boolean(JSON.parse(storedAuth));
        // Only set authenticated state if it's true
        if (isAuth) {
          setIsAuthenticated(true);
        } else {
          // If stored value is false, treat as not authenticated (null)
          setIsAuthenticated(null);
        }
      }
      // No else clause - if no stored auth, keep as null (not authenticated)
    }
  }, [searchParams]);

  // Update localStorage only when authenticated
  useEffect(() => {
    if (isAuthenticated === true) {
      localStorage.setItem('isAuthed', JSON.stringify(true));
    }
  }, [isAuthenticated]);

  const logout = () => {
    localStorage.removeItem('isAuthed');
    setIsAuthenticated(null);
  };

  return (
    <AuthContext.Provider value={{isAuthenticated, setIsAuthenticated, logout}}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
